import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { videosQueryOptions, videoTagsQueryOptions } from "@/api-queries/videos";
import { VideoCard } from "@/components/video/VideoCard";
import { VideoTagChips } from "@/components/video/VideoTagChips";
import { Pagination } from "@/components/ui/pagination";
import { defineSection } from "@/ssr/define-section";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

const videoMeta = definePageMeta({
  titleKey: "video.meta.title",
  descriptionKey: "video.meta.description",
  ogImage: "/og-default.jpg",
  structuredData: (ctx) =>
    buildPageStructuredData(ctx, {
      titleKey: "video.meta.title",
      descriptionKey: "video.meta.description",
      ogImage: "/og-default.jpg",
      pageType: "CollectionPage",
      breadcrumbs: [
        { name: "Psihotron", path: "/" },
        { name: ctx.t("nav.video"), path: "/video" },
      ],
    }),
});

const PER_PAGE = 12;

interface VideoSearch {
  tag?: string;
  q?: string;
  page?: number;
}

function listOptions(search: VideoSearch) {
  return videosQueryOptions({
    limit: PER_PAGE,
    page: search.page ?? 1,
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.q ? { q: search.q } : {}),
  });
}

function VideoIndexView() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/video" });
  const search = Route.useSearch();
  const { tag, q, page = 1 } = search;

  const [queryInput, setQueryInput] = useState(q ?? "");
  useEffect(() => setQueryInput(q ?? ""), [q]);
  useEffect(() => {
    const trimmed = queryInput.trim();
    if (trimmed === (q ?? "")) return;
    const id = setTimeout(() => {
      navigate({ search: (prev: VideoSearch) => ({ ...prev, q: trimmed || undefined, page: undefined }) });
    }, 300);
    return () => clearTimeout(id);
  }, [queryInput]);

  const { data, isLoading, error } = useQuery(listOptions(search));
  const { data: tagsData } = useQuery(videoTagsQueryOptions);
  const videos = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const setTag = (next?: string) =>
    navigate({ search: (prev: VideoSearch) => ({ ...prev, tag: next, page: undefined }) });
  const setPage = (next: number) =>
    navigate({ search: (prev: VideoSearch) => ({ ...prev, page: next === 1 ? undefined : next }) });

  return (
    <div className="min-h-screen">
      <section className="py-16 sm:py-20" style={{ background: "oklch(98% 0.005 84)" }}>
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h1
            className="text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {t("video.title")}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">{t("video.subtitle")}</p>

          <div className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 shadow-sm">
            <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={t("video.searchPlaceholder")}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </section>

      {(tagsData?.data.length ?? 0) > 0 && (
        <div className="border-b border-border bg-white/60">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <VideoTagChips tags={tagsData!.data} active={tag} allLabel={t("video.allTags")} onChange={setTag} />
          </div>
        </div>
      )}

      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {isLoading && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="aspect-video bg-muted" />
                  <div className="space-y-3 p-5">
                    <div className="h-3 w-24 rounded bg-muted" />
                    <div className="h-5 w-full rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && <p className="py-12 text-center text-muted-foreground">{t("video.loadError")}</p>}

          {!isLoading && !error && videos.length === 0 && (
            <p className="py-12 text-center text-muted-foreground">{q || tag ? t("video.noResults") : t("video.noVideos")}</p>
          )}

          {!isLoading && videos.length > 0 && (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {videos.map((video) => (
                  <VideoCard key={video.id} video={video} onTagClick={setTag} />
                ))}
              </div>
              {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const VideoIndexSection = defineSection({
  Component: VideoIndexView,
  queries: [listOptions({}), videoTagsQueryOptions],
});

function VideoIndexPage() {
  return <VideoIndexSection.Component />;
}

export const Route = createFileRoute("/video/")({
  ...ssrPage({
    component: VideoIndexPage,
    sections: [VideoIndexSection],
    meta: videoMeta,
  }),
  validateSearch: (search: Record<string, unknown>): VideoSearch => ({
    tag: typeof search.tag === "string" && /^[a-z0-9-]{1,60}$/.test(search.tag) ? search.tag : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q.trim() : undefined,
    page: typeof search.page === "number" && search.page > 1 ? search.page : undefined,
  }),
});
